import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const HERO_LINES = [
  { text: "Sermon clips" },
  { text: "your church" },
  { text: "actually shares.", italic: true },
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBackground = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const yForeground = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid={TEST_IDS.hero.root}
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Ambient warm blobs */}
      <motion.div
        style={{ y: yBackground }}
        aria-hidden="true"
        className="absolute inset-0 -z-10"
      >
        <div className="blob" style={{ background: "#E07A5F", width: 520, height: 520, top: -80, right: -120 }} />
        <div className="blob" style={{ background: "#D4AF37", width: 420, height: 420, bottom: -100, left: -80, opacity: 0.35 }} />
        <div className="blob" style={{ background: "#264653", width: 300, height: 300, top: "40%", left: "45%", opacity: 0.15 }} />
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] animate-pulse" />
          <span className="eyebrow">A studio for smaller churches</span>
        </motion.div>

        {/* Kinetic headline */}
        <motion.h1
          style={{ y: yForeground, opacity }}
          className="font-serif-display font-black tracking-tight text-[#1C1C1A] text-[clamp(48px,9vw,132px)] leading-[0.95] max-w-[16ch]"
        >
          {HERO_LINES.map((l, i) => (
            <span key={i} className="line-mask">
              <motion.span
                className={`line-inner ${l.italic ? "italic text-[#E07A5F]" : ""}`}
                custom={i}
                initial="hidden"
                animate="show"
                variants={lineVariants}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        {/* Sub + CTA row */}
        <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 md:gap-16 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="md:col-span-6 text-[17px] md:text-[19px] text-[#1C1C1A]/80 leading-relaxed max-w-xl"
          >
            We turn full-length Sunday sermons into short, captioned vertical
            clips — the kind your congregation actually stops scrolling for.
            Send the sermon, we handle the rest.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <a
              href="#pricing"
              data-testid={TEST_IDS.hero.ctaPricing}
              className="btn-pill btn-primary text-[14px]"
            >
              See pricing
              <ArrowUpRight size={16} />
            </a>
            <a
              href="#book"
              data-testid={TEST_IDS.hero.ctaCall}
              className="btn-pill btn-ghost text-[14px]"
            >
              Get in touch
            </a>
          </motion.div>
        </div>

        {/* Hero visual */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-16 md:mt-24 grid md:grid-cols-12 gap-6 items-center"
        >
          <div
            data-testid={TEST_IDS.hero.demoBadge}
            className="md:col-span-9 relative rounded-3xl overflow-hidden border border-black/5 bg-[#F3EFE9] aspect-[16/8] group"
          >
            <img
              src="https://images.unsplash.com/photo-1544531586-fde5298cdd40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxwYXN0b3IlMjBzcGVha2luZyUyMHN0YWdlfGVufDB8fHx8MTc4NDU3ODcyNnww&ixlib=rb-4.1.0&q=85"
              alt="Pastor speaking to a warm congregation"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
            />
          </div>

          <div className="md:col-span-3 space-y-4">
            <div className="flex items-baseline gap-3">
              <span className="text-5xl md:text-6xl font-serif-display">48h</span>
              <span className="eyebrow">avg turnaround</span>
            </div>
            <div className="hairline" />
            <p className="text-[14px] text-[#1C1C1A]/70 leading-relaxed">
              Send the sermon Sunday night. See the clips by Tuesday.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
